import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, UntypedFormControl, Validators } from "@angular/forms";
import { MatCardModule, } from "@angular/material/card";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { AsyncPipe } from "@angular/common";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatIconModule } from "@angular/material/icon";
import { debounceTime, distinctUntilChanged, Observable, startWith, Subject, switchMap, takeUntil } from "rxjs";
import { UserDTO } from "../../../model/user/user-dto.model";
import { map } from "rxjs/operators";
import { UsersService } from "../../../services/users.service";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatTimepickerModule } from "@angular/material/timepicker";
import { MatNativeDateModule } from "@angular/material/core";
import { MatButton, MatIconButton } from "@angular/material/button";
import { MatSelect } from "@angular/material/select";
import { MatTooltipModule } from "@angular/material/tooltip";
import { AppointmentService } from "../../../services/appointment.service";
import { AppointmentDTO } from "../../../model/appointment/appointmentDTO.model";
import { AppointmentRequestService } from "../../../services/appointmentRequest.service";
import { FuseAlertComponent } from "../../../../@fuse/components/alert";
import { EstablishmentBranding, EstablishmentParamService } from "../../../services/establishment-param.service";

@Component({
    selector: 'appointment-controller',
    templateUrl: 'appointment.component.html',
    styleUrls: ['appointment.component.scss'],
    imports: [
        ReactiveFormsModule,
        AsyncPipe,
        MatCardModule,
        MatFormFieldModule,
        MatInputModule,
        MatAutocompleteModule,
        MatIconModule,
        MatDatepickerModule,
        MatTimepickerModule,
        MatNativeDateModule,
        MatButton,
        MatIconButton,
        MatSelect,
        MatTooltipModule,
        FuseAlertComponent,
    ]
})

export class AppointmentComponent implements OnInit, OnDestroy {
    appointmentForm: FormGroup;
    doctorOptions$: Observable<UserDTO[]>;
    inputDoctor: UntypedFormControl = new UntypedFormControl('', [Validators.required]);
    minDate = new Date();

    dayTimes = [];

    takenTimes: string[] = [];

    dateInput: UntypedFormControl = new UntypedFormControl();

    successMessage: boolean = false;
    errorMessage: boolean = false;
    noDoctorPreference: boolean = false;

    branding: EstablishmentBranding;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(private fb: FormBuilder,
        private usersService: UsersService,
        private appointmentService: AppointmentService,
        private appointmentRequestService: AppointmentRequestService,
        private establishmentParamService: EstablishmentParamService) {

        this.branding = this.establishmentParamService.branding;

        this.appointmentForm = this.fb.group({
            firstName: ['', [Validators.required]],
            lastName: ['', [Validators.required]],
            phone: ['', [Validators.required, Validators.pattern("[0-9]{10}")]],
            time: ['', [Validators.required]],
            motif: ['', [Validators.required]]
        });
        this.dayTimes = this.generateTimes();
        this.initDoctor();
    }

    ngOnInit() {
        this.establishmentParamService.branding$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((branding: EstablishmentBranding) => {
                this.branding = branding;
            });
        this.establishmentParamService.load().subscribe();

        this.doctorOptions$ = this.inputDoctor.valueChanges.pipe(
            startWith(''),
            debounceTime(300),
            distinctUntilChanged(),
            switchMap((searchPhrase: string) => {
                return this.usersService.getPublicDoctors(searchPhrase, 0, 100).pipe(
                    map((response: any) => Array.isArray(response) ? response : response.content)
                )
            }));

        this.dateInput.valueChanges.subscribe((val) => {
            let startDate = new Date(val);
            let endDate = new Date(val);
            startDate.setHours(0)
            endDate.setHours(24);
            this.appointmentService.getAppointments(startDate.toISOString(), endDate.toISOString(), this.appointmentForm.value.doctor).subscribe((list) => {
                this.takenTimes = this.getListTakenTime(list);
            })
        });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    initDoctor() {
        this.doctorOptions$ = this.usersService.getPublicDoctors("", 0, 100);
    }

    displayUser(user): string {
        return user ? `${user.lastName} ${user.firstName}` : '';
    }

    bookedDates = [];

    dateFilter = (date: Date | null): boolean => {
        if (!date) return false;

        // On met l'heure à minuit pour comparer uniquement le jour
        const dateCopy = new Date(date);
        dateCopy.setHours(0, 0, 0, 0);

        return !this.bookedDates.includes(dateCopy.getTime());
    };

    generateTimes() {
        const times: string[] = [];
        for (let h = 0; h <= 23; h++) {
            for (let m of [0, 30]) {
                const hour = h.toString().padStart(2, '0');
                const min = m.toString().padStart(2, '0');
                times.push(`${hour}:${min}`);
            }
        }
        return times;
    }

    isTimeDisabled(time) {
        return this.takenTimes.includes(time);
    }

    getListTakenTime(appointments: AppointmentDTO[]) {
        let list = [];
        for (let t of appointments) {
            let startDate = new Date(t.startDate);
            let endDate = new Date(t.endDate);

            let start = startDate.getHours();
            let end = endDate.getHours() - 1;
            if (startDate.getMinutes() != 0) {
                start = start + 1;
                const startString = startDate.getHours().toString();
                list.push((startString.length == 1 ? "0" + startString : startString) + ":30");
            }
            if (endDate.getMinutes() != 0) {
                const endString = endDate.getHours().toString()
                list.push((endString.length == 1 ? "0" + endString : endString) + ":00");
            }

            for (let h = start; h <= end; h++) {
                for (let m of [0, 30]) {
                    const hour = h.toString().padStart(2, '0');
                    const min = m.toString().padStart(2, '0');
                    list.push(`${hour}:${min}`);
                }
            }
        }
        return list;
    }

    toggleDoctorPreference(): void {
        this.noDoctorPreference = !this.noDoctorPreference;
        if (this.noDoctorPreference) {
            this.inputDoctor.setValue(null);
            this.inputDoctor.clearValidators();
            this.inputDoctor.disable();
        } else {
            this.inputDoctor.setValidators([Validators.required]);
            this.inputDoctor.enable();
        }
        this.inputDoctor.updateValueAndValidity();
    }

    sendAppointmentRequest() {
        if (this.appointmentForm.valid) {

            const date = new Date(this.dateInput.value);
            const time = new Date();
            time.setHours(this.appointmentForm.value.time.split(":")[0]);
            time.setMinutes(this.appointmentForm.value.time.split(":")[1]);

            const newAppointmentRequest = {
                firstName: this.appointmentForm.value.firstName,
                lastName: this.appointmentForm.value.lastName,
                phone: this.appointmentForm.value.phone,
                startDate: this.parseDateTime(date, time),
                doctor: this.noDoctorPreference ? null : this.inputDoctor.value,
                motif: this.appointmentForm.value.motif,
                id: null
            };

            this.appointmentRequestService.createAppointmentRequest(newAppointmentRequest).subscribe({
                next: () => {
                    console.log("Rendez-vous crée avec succée");
                    this.errorMessage = false;
                    this.successMessage = true;
                },
                error: () => {
                    console.log("Erreur création rendez-vous");
                    this.errorMessage = true;
                    this.successMessage = false;
                }
            });
        }
    }

    parseDateTime(date: Date, time: Date) {
        if (time) {
            date.setHours(time.getHours());
            date.setMinutes(time.getMinutes());
        }
        else {
            date.setHours(date.getHours() + 1);
        }
        return date.toLocaleString();
    }
}